<script setup lang="ts">
import { Quria, BungieMembershipType, GroupType, GroupV2, GroupsForMemberFilter } from 'quria'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { type AccountResult, type Clan, formatUsername } from '@wastedondestiny/destiny-library'
import AccountPicker from './AccountPicker.vue'

const emit = defineEmits(['search', 'error'])

const searchTerm = ref('')
const loading = ref(false)
const accounts = ref([] as AccountResult[])
const clan = ref<GroupV2>()
const searchBar = ref<HTMLInputElement | null>(null)
const quria = new Quria({
  API_KEY: import.meta.env.VITE_BUNGIE_API_KEY
})
const MAX_RESULTS = 8
let controller: AbortController|undefined

interface DestinyReportPlayer {
  mid: string
  mtype: number
}

function canAddMore() {
  return accounts.value.length < MAX_RESULTS
}

function addResults(newAccounts: AccountResult[]) {
  for (const account of newAccounts) {
    if (!canAddMore()) break
    if (!accounts.value.find(x => x.membershipId === account.membershipId)) {
      accounts.value.push(account)
    }
  }
}

async function getProfile(membershipType: number, membershipId: string) {
  const profile = await quria.destiny2.GetLinkedProfiles(membershipId, membershipType, { getAllMemberships: true })
  const accountResults: AccountResult[] = []

  for (const userInfo of profile.Response?.profiles || []) {
    if (!accounts.value.find(x => x.membershipId === userInfo.membershipId)) {
      accountResults.push({
        displayName: formatUsername(userInfo.bungieGlobalDisplayName, userInfo.bungieGlobalDisplayNameCode || 0, userInfo.displayName),
        membershipId: userInfo.membershipId,
        crossSaveOverride: userInfo.crossSaveOverride,
        dateLastPlayed: new Date(userInfo.dateLastPlayed),
        membershipType: userInfo.membershipType
      })
    }
  }

  return accountResults
}

async function prefixSearch(value: string) {
  const search = await quria.user.SearchByGlobalNamePost(0, value)
  let accountResults: AccountResult[] = []

  for (const results of search.Response?.searchResults || []) {
    if (!canAddMore()) break
    const userInfo = results?.destinyMemberships?.[0]

    if (userInfo) accountResults = [
      ...accountResults,
      ...await getProfile(userInfo.membershipType, userInfo.membershipId)
    ]
  }

  return accountResults
}

async function exactSearch(value: string) {
  const [displayName, displayNameCode] = value.split('#')
  const search = await quria.destiny2.SearchDestinyPlayerByBungieName(BungieMembershipType.All, displayName, parseInt(displayNameCode, 10))
  const userInfo = search.Response?.[0]

  if (userInfo) return await getProfile(userInfo.membershipType, userInfo.membershipId)

  return []
}

async function destinyReportSearch(value: string, signal?: AbortSignal) {
  const [displayName, displayNameCode] = value.split('#')
  const query = `${encodeURIComponent(displayName)}${displayNameCode ? `%23${displayNameCode}` : ''}`
  const search = await fetch(`https://i.destiny.report/api/search?q=${query}`, { signal })
  const results = await search.json() as { players: DestinyReportPlayer[]|null }
  let accountResults: AccountResult[] = []

  for (const player of results.players || []) {
    if (!canAddMore()) break
    if (!accounts.value.find(x => x.membershipId === player.mid)) {
      accountResults = [
        ...accountResults,
        ...await getProfile(player.mtype, player.mid)
      ]
    }
  }

  return accountResults
}

async function elasticSearch(value: string, signal?: AbortSignal) {
  const [displayName, displayNameCode] = value.split('#')
  const search = await fetch(`https://elastic.destinytrialsreport.com/players/0/${(displayNameCode ? `${displayName}%23${displayNameCode}` : displayName)}/`, {
    signal
  })
  const results = await search.json()
  const accountResults: AccountResult[] = []

  for (const result of results || []) {
    if (!canAddMore()) break
    accountResults.push({
      displayName: result.bungieName || result.displayName,
      membershipId: result.membershipId,
      membershipType: result.membershipType,
      crossSaveOverride: result.crossSaveOverride.membershipType,
      dateLastPlayed: new Date(result.lastPlayed)
    })
  }

  return accountResults
}

async function clanSearch(value: string) {
  const search = await quria.groupv2.GetGroupByNameV2(value, GroupType.Clan)
  if (search.Response) clan.value = search.Response.detail
}

async function search() {
  controller?.abort()
  accounts.value = []
  clan.value = undefined

  if (!searchTerm.value) return

  loading.value = true
  controller = new AbortController()
  const signal = controller.signal
  const [, displayNameCode] = searchTerm.value.split('#')

  const tasks = displayNameCode
    ? [
      exactSearch(searchTerm.value),
      destinyReportSearch(searchTerm.value, signal),
      elasticSearch(searchTerm.value, signal)
    ]
    : [
      prefixSearch(searchTerm.value),
      destinyReportSearch(searchTerm.value, signal),
      elasticSearch(searchTerm.value, signal)
    ]

  let pending = tasks.length

  for (const task of tasks) {
    task
      .then(addResults)
      .catch(() => {})
      .finally(() => {
        pending -= 1
        if (!canAddMore()) controller?.abort()
        if (pending === 0 || !canAddMore()) loading.value = false
      })
  }

  await clanSearch(searchTerm.value)
}

async function select(account: AccountResult|null) {
  controller?.abort()
  accounts.value = []
  clan.value = undefined
  searchTerm.value = ''

  if (!account) return

  const groups = await quria.groupv2.GetGroupsForMember(GroupsForMemberFilter.All, GroupType.Clan, account.membershipId, account.membershipType!)
  const group = groups.Response?.results?.[0]?.group

  if (group) {
    emit('search', { name: group.name, id: group.groupId } as Clan)
  } else {
    emit('error', new Error(`${account.displayName} is not in a clan.`))
  }
}

function selectClan(selectedClan: Clan) {
  accounts.value = []
  clan.value = undefined
  searchTerm.value = ''
  emit('search', selectedClan)
}

function cancel() {
  controller?.abort()
  accounts.value = []
  clan.value = undefined
  searchTerm.value = ''
}

function onEnterPress(event: KeyboardEvent) {
  if (event.key === 'Enter') search()
}

onMounted(() => { searchBar.value?.addEventListener('keypress', onEnterPress) })
onBeforeUnmount(() => { searchBar.value?.removeEventListener('keypress', onEnterPress) })
</script>

<template>
  <div class="flex flex-col items-center relative">
    <div class="relative">
      <input class="py-2 pl-4 pr-28 w-72 md:w-96 h-12 rounded-3xl bg-white outline-none" autocomplete="off"
        aria-label="Search bar" type="text" ref="searchBar" placeholder="Username or clan name…" v-model="searchTerm" />
      <div v-if="loading" class="absolute top-0 right-0 w-24 h-10 p-1 m-1 rounded-3xl bg-[#cdad33] text-zinc-100 text-3xl leading-7 font-serif font-bold flex gap-2 justify-center">
        <span class="transition-opacity bullet-1">&bullet;</span>
        <span class="transition-opacity bullet-2">&bullet;</span>
        <span class="transition-opacity bullet-3">&bullet;</span>
      </div>
      <button v-else class="absolute top-0 right-0 w-24 h-10 p-2 m-1 rounded-3xl bg-[#cdad33] text-white hover:bg-[#cdad33]" @click="search">SEARCH</button>
    </div>
    <AccountPicker :accounts="accounts" :clan="clan" @select="select" @select-clan="selectClan" @cancel="cancel" />
  </div>
</template>
